import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { ExternalLink, X, Globe2, FileText, Scroll, Search, Mail, BadgeCheck } from "lucide-react";

const certificates = [
  {
    id: "iec",
    icon: Globe2,
    title: "Importer-Exporter Code (IEC)",
    issuer: "Directorate General of Foreign Trade, Govt. of India",
    meta: [
      { label: "IEC Number", value: "AQAPR7398L" },
      { label: "Date of Issue", value: "10 May 2026" },
      { label: "Firm Name", value: "Prithira Global" },
      { label: "Nature", value: "Proprietorship" },
    ],
    bullets: [
      "Authorises Prithira Global to legally export goods to any country worldwide",
      "Mandatory government registration for all Indian exporters — issued by DGFT, Ministry of Commerce",
      "Recognised by Indian Customs for all international shipments including Bhutan, Southeast Asia, Europe",
      "Independently verifiable at dgft.gov.in by entering IEC number AQAPR7398L",
    ],
    image: "/credentials/iec.png",
    verifyUrl: "https://www.dgft.gov.in/CP/?opt=view-any-iec",
    verifyText: "Verify at dgft.gov.in",
  },
  {
    id: "gst",
    icon: FileText,
    title: "GST Registration",
    issuer: "Goods & Services Tax, Government of India",
    meta: [
      { label: "GSTIN", value: "19AQAPR7398L1ZW" },
      { label: "Date of Issue", value: "25 March 2026" },
      { label: "Legal Name", value: "Prithira Global" },
      { label: "Type", value: "Regular" },
    ],
    bullets: [
      "Mandatory Indian tax registration for businesses crossing the turnover threshold",
      "Enables compliant invoicing for domestic B2B and B2C transactions",
      "Required for GST input credit eligibility and tax filings",
      "Verifiable at gst.gov.in by entering GSTIN 19AQAPR7398L1ZW",
    ],
    image: "/credentials/gst-page1.png",
    verifyUrl: "https://services.gst.gov.in/services/searchtp",
    verifyText: "Verify at gst.gov.in",
  },
  {
    id: "trade-license",
    icon: Scroll,
    title: "Trade License",
    issuer: "South Dum Dum Municipality, West Bengal",
    meta: [
      { label: "License No.", value: "0917P111526451418" },
      { label: "Date of Issue", value: "16 March 2026" },
      { label: "Valid Until", value: "15 March 2029" },
      { label: "Holder", value: "Prithira Global" },
    ],
    bullets: [
      "Permanent Certificate of Enlistment issued under West Bengal Municipal Act, 1993 (Section 118)",
      "Authorises Prithira Global to legally conduct trade as a General Order Supplier",
      "Confirms legitimate business premises and operating compliance in Kolkata jurisdiction",
      "Verifiable online at edistrict.wb.gov.in via the unique certificate reference",
    ],
    image: "/credentials/trade-license.png",
    verifyUrl: "https://edistrict.wb.gov.in/",
    verifyText: "Verify at edistrict.wb.gov.in",
  },
  {
    id: "udyam",
    icon: BadgeCheck,
    title: "Udyam Registration (MSME)",
    issuer: "Ministry of Micro, Small & Medium Enterprises, Govt. of India",
    meta: [
      { label: "Udyam Number", value: "UDYAM-WB-14-0265229" },
      { label: "Date of Registration", value: "26 March 2026" },
      { label: "Enterprise Type", value: "Micro" },
      { label: "Major Activity", value: "Trading" },
    ],
    bullets: [
      "Government of India recognition as a Micro Enterprise under the MSME Development Act, 2006",
      "Qualifies Prithira Global for MSME priority benefits, government tenders, and credit facilitation",
      "Confirms registered business standing for export and procurement compliance",
      "Verifiable online at udyamregistration.gov.in via the Udyam Number",
    ],
    image: "/credentials/udyam.png",
    verifyUrl: "https://udyamregistration.gov.in/Udyam_Verify.aspx",
    verifyText: "Verify at udyamregistration.gov.in",
  },
];

const PRODUCT_BADGES = [
  "EN 13432",
  "BPI Certified",
  "ISO 17088",
  "FSSAI Compliant",
  "SGS Tested",
  "PFAS Free",
  "EU Food Contact Safe",
  "Oil & Water Resistant",
];

const credentialsJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Prithira Global",
  url: "https://www.prithiraglobal.com/credentials",
  identifier: [
    {
      "@type": "PropertyValue",
      propertyID: "IEC (Importer-Exporter Code)",
      value: "AQAPR7398L",
    },
    {
      "@type": "PropertyValue",
      propertyID: "GSTIN",
      value: "19AQAPR7398L1ZW",
    },
    {
      "@type": "PropertyValue",
      propertyID: "Trade License (South Dum Dum Municipality)",
      value: "0917P111526451418",
    },
    {
      "@type": "PropertyValue",
      propertyID: "Udyam Registration (MSME)",
      value: "UDYAM-WB-14-0265229",
    },
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Government Registration",
      name: "Importer-Exporter Code (IEC)",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "Directorate General of Foreign Trade, Government of India",
      },
      identifier: "AQAPR7398L",
      dateCreated: "2026-05-10",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Tax Registration",
      name: "GST Registration",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "Goods & Services Tax, Government of India",
      },
      identifier: "19AQAPR7398L1ZW",
      dateCreated: "2026-03-25",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Municipal Trade License",
      name: "Permanent Certificate of Enlistment (Trade License)",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "South Dum Dum Municipality, West Bengal",
      },
      identifier: "0917P111526451418",
      dateCreated: "2026-03-16",
      expires: "2029-03-15",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "MSME Registration",
      name: "Udyam Registration (Micro Enterprise)",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "Ministry of Micro, Small & Medium Enterprises, Government of India",
      },
      identifier: "UDYAM-WB-14-0265229",
      dateCreated: "2026-03-26",
    },
  ],
};

const CredentialsPage = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="credentials-page" className="noise-overlay">
      <Helmet
        title="Our Credentials | Prithira Global"
        meta={[
          {
            name: "description",
            content:
              "Verified government-issued credentials of Prithira Global — IEC AQAPR7398L, GSTIN 19AQAPR7398L1ZW, Udyam MSME registration, and South Dum Dum Municipality Trade License. Independently verifiable on official Government of India portals.",
          },
          {
            name: "keywords",
            content:
              "Prithira Global IEC AQAPR7398L, GSTIN 19AQAPR7398L1ZW, Udyam MSME UDYAM-WB-14-0265229, verified exporter India, biodegradable packaging exporter credentials, trade license Kolkata, DGFT IEC",
          },
          { property: "og:title", content: "Our Credentials | Prithira Global" },
          {
            property: "og:description",
            content:
              "Government-issued credentials and supplier product certifications — IEC, GST, Trade License.",
          },
          {
            property: "og:url",
            content: "https://www.prithiraglobal.com/credentials",
          },
        ]}
        link={[
          {
            rel: "canonical",
            href: "https://www.prithiraglobal.com/credentials",
          },
        ]}
        script={[
          {
            type: "application/ld+json",
            innerHTML: JSON.stringify(credentialsJsonLd),
          },
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#1B3A2B] text-white">
        <div className="container-custom text-center">
          <span className="caption text-[#E8C06A] mb-3 block tracking-widest">
            VERIFIED CREDENTIALS
          </span>
          <h1 className="font-heading text-3xl md:text-5xl text-white mb-4">
            Our <span className="text-[#E8C06A] italic">Credentials</span>
          </h1>
          <p className="text-[#A8C8A8] text-sm md:text-base max-w-2xl mx-auto font-light">
            All credentials below are government-issued, independently verifiable
            documents in the name of Prithira Global. Tap any certificate image to
            view full size.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16 bg-[#F6F1E8]">
        <div className="container-custom max-w-4xl">
          {/* Tip */}
          <div className="flex gap-3 bg-[rgba(196,144,42,0.08)] border border-[rgba(196,144,42,0.2)] rounded-xl px-4 py-3 mb-8">
            <Search className="w-5 h-5 text-[#C4902A] shrink-0 mt-0.5" />
            <p className="text-[#C4902A] text-sm font-medium leading-relaxed">
              Every certificate below carries a unique government reference number.
              Use the verification link beneath each card to confirm authenticity on
              the official portal.
            </p>
          </div>

          {/* Certificate cards */}
          {certificates.map((cert) => {
            const Icon = cert.icon;
            return (
              <article
                key={cert.id}
                data-testid={`cert-card-${cert.id}`}
                className="bg-white border border-[rgba(60,107,74,0.15)] rounded-2xl mb-6 overflow-hidden shadow-sm"
              >
                {/* Header */}
                <div className="flex items-center gap-3 bg-[#1B3A2B] px-4 py-3">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(232,192,106,0.2)] flex items-center justify-center text-[#E8C06A] shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-sm md:text-base leading-tight">
                      {cert.title}
                    </h2>
                    <p className="text-[#A8C8A8] text-xs mt-0.5">{cert.issuer}</p>
                  </div>
                </div>

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3 px-4 py-4 border-b border-[rgba(60,107,74,0.15)]">
                  {cert.meta.map((m) => (
                    <div key={m.label}>
                      <div className="font-mono text-[10px] text-[#6B7B6B] uppercase tracking-wider mb-1">
                        {m.label}
                      </div>
                      <div className="text-sm font-semibold text-[#0F1A0F]">
                        {m.value || (
                          <span className="text-[#6B7B6B] font-normal italic">
                            to be filled
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bullets */}
                <div className="px-4 py-4 border-b border-[rgba(60,107,74,0.15)]">
                  {cert.bullets.map((b, i) => (
                    <div key={i} className="flex gap-2 mb-2 last:mb-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3A6B4A] shrink-0 mt-2"></span>
                      <span className="text-sm text-[#0F1A0F] leading-relaxed">
                        {b}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Image */}
                <div className="px-4 py-4">
                  <div className="font-mono text-[10px] text-[#6B7B6B] uppercase tracking-wider mb-2 flex items-center gap-2">
                    Official Certificate
                    <span className="flex-1 h-px bg-[rgba(60,107,74,0.15)]"></span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setLightbox({ src: cert.image, label: cert.title })
                    }
                    className="block w-full cursor-zoom-in bg-stone-50 rounded-lg overflow-hidden"
                    data-testid={`cert-img-${cert.id}`}
                  >
                    <img
                      src={cert.image}
                      alt={`${cert.title} certificate for Prithira Global`}
                      className="w-full rounded-lg border border-[rgba(60,107,74,0.15)] shadow-sm hover:opacity-90 transition-opacity"
                      loading="lazy"
                    />
                  </button>
                  <a
                    href={cert.verifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 w-full bg-[#3A6B4A] hover:bg-[#1B3A2B] transition-colors text-white text-sm font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2"
                    data-testid={`cert-verify-${cert.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {cert.verifyText}
                  </a>
                </div>
              </article>
            );
          })}

          <div className="h-px bg-[rgba(60,107,74,0.15)] my-8"></div>

          {/* Product certifications */}
          <h2 className="text-[#1B3A2B] font-bold text-lg mb-2">
            Product Certifications
          </h2>
          <p className="text-[#6B7B6B] text-sm leading-relaxed mb-5">
            Products supplied by Prithira Global are sourced from manufacturers
            holding these internationally recognised certifications. Full
            documentation available on request.
          </p>

          <div className="flex flex-wrap gap-2 bg-white border border-[rgba(60,107,74,0.15)] rounded-xl p-4 mb-6">
            {PRODUCT_BADGES.map((b) => (
              <span
                key={b}
                className="text-xs font-semibold py-1.5 px-3 rounded-full bg-[rgba(60,107,74,0.08)] text-[#3A6B4A]"
              >
                {b}
              </span>
            ))}
          </div>

          <div className="flex gap-3 bg-[rgba(196,144,42,0.08)] border border-[rgba(196,144,42,0.2)] rounded-xl px-4 py-3 mb-2">
            <Mail className="w-5 h-5 text-[#C4902A] shrink-0 mt-0.5" />
            <p className="text-[#C4902A] text-sm font-medium leading-relaxed">
              Product certification documentation from our manufacturers is
              available on request for institutional buyers, export compliance,
              and due diligence purposes. Contact{" "}
              <Link
                to="/contact"
                className="underline decoration-dotted underline-offset-2"
              >
                info@prithiraglobal.com
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[1000] bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="cert-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-white flex items-center justify-center"
            aria-label="Close"
            data-testid="cert-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.label}
            className="max-w-full max-h-[85vh] rounded-md object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="text-white/60 text-xs mt-3 text-center">
            {lightbox.label}
          </div>
        </div>
      )}
    </div>
  );
};

export default CredentialsPage;
